/**
 * Backend Error Message Parser
 *
 * Parses backend error messages and returns user-friendly versions
 * with actionable guidance.
 */

/**
 * Parses backend error messages and returns user-friendly versions
 *
 * @param {string} errorMessage - Raw error from backend
 * @returns {Object} { message: string, severity: 'error' | 'warning', action: string | null }
 */
export function parseBackendError(errorMessage) {
    const msg = errorMessage?.toLowerCase() || '';

    // Tariff validation errors
    if (msg.includes('electrictariff') && msg.includes('required')) {
        return {
            message: 'Your scenario is missing tariff information. On-grid systems require a utility tariff to calculate costs.',
            severity: 'error',
            action: 'Go to the Utility tab and select a tariff or enter custom rates.'
        };
    }

    if (msg.includes('exactly one of') || msg.includes('conflicting tariff')) {
        return {
            message: 'Tariff configuration error: Multiple or conflicting tariff types detected.',
            severity: 'error',
            action: 'Please clear your tariff selection and choose only one: structured tariff, blended rate, or TOU rates.'
        };
    }

    // Load profile errors
    if (msg.includes('load') && msg.includes('8760')) {
        return {
            message: 'Load profile must be exactly 8760 hours (one full year).',
            severity: 'error',
            action: 'Go to the Load tab and upload or generate a complete annual load profile.'
        };
    }

    // Geocoding errors
    if (msg.includes('geocode') || msg.includes('coordinates')) {
        return {
            message: 'Location could not be geocoded. Check that your address or zip code is valid.',
            severity: 'error',
            action: 'Go to the Site tab and verify your location information.'
        };
    }

    // Infeasibility errors
    if (msg.includes('infeasible') || msg.includes('no solution')) {
        return {
            message: 'Optimization could not find a feasible solution with the given constraints.',
            severity: 'warning',
            action: 'Try relaxing constraints (e.g., increase grid import limits, adjust technology sizes).'
        };
    }

    // NREL API errors
    if (msg.includes('nrel') || msg.includes('reopt')) {
        return {
            message: 'The NREL REopt API encountered an error processing your scenario.',
            severity: 'error',
            action: 'Check your inputs for invalid values or try again later. Contact support if the issue persists.'
        };
    }

    // Timeout errors
    if (msg.includes('timeout') || msg.includes('timed out')) {
        return {
            message: 'The optimization request timed out. This usually means the problem is too complex.',
            severity: 'error',
            action: 'Try simplifying the scenario or contact support for assistance.'
        };
    }

    // Rate limit errors
    if (msg.includes('rate limit') || msg.includes('too many requests')) {
        return {
            message: 'Rate limit exceeded. Please wait before submitting another optimization.',
            severity: 'warning',
            action: 'Wait a few minutes and try again.'
        };
    }

    // Default: return original message
    return {
        message: errorMessage,
        severity: 'error',
        action: null
    };
}
